import {useEffect, useRef} from 'react';
import {scrollIntoView} from '@pb/kit-util-scroll-into-view';

const ACTIVE_TAB_SELECTOR = '[aria-selected="true"]';

function scrollToActiveTab(parent: Element | null) {
    const activeTab = parent?.querySelector<HTMLAnchorElement>(ACTIVE_TAB_SELECTOR);

    if (activeTab) {
        scrollIntoView(activeTab, {
            block: 'nearest',
            inline: 'nearest',
            container: 'nearest',
        });
    }
}

function hasOverflow(container: HTMLDivElement | null) {
    if (!container) {
        return false;
    }

    return container.scrollWidth > container.clientWidth;
}

interface UseScrollableTabsProps {
    enableScroll?: boolean,
    contentRef: React.RefObject<HTMLUListElement | null>,
    containerRef: React.RefObject<HTMLDivElement | null>,
}

/**
 * For async loading of tabs and resizing, ensure the active tab stays in view.
 */
export const useScrollableTabs = ({
    enableScroll = true,
    contentRef,
    containerRef,
}: UseScrollableTabsProps) => {

    const prevHadOverflowRef = useRef<boolean>(false);

    useEffect(() => {
        if (!enableScroll) {
            prevHadOverflowRef.current = false;
            return;
        }

        const maybeScrollActiveIntoView = () => {
            const nextHasOverflow = hasOverflow(containerRef?.current);
            if (nextHasOverflow) {
                scrollToActiveTab(contentRef.current);
            }

            prevHadOverflowRef.current = nextHasOverflow;
        };

        maybeScrollActiveIntoView();

        const resizeObserver = new ResizeObserver(() => {
            requestAnimationFrame(() => {
                maybeScrollActiveIntoView();
            });
        });

        if (contentRef.current) {
            resizeObserver.observe(contentRef.current);
        }

        return () => {
            resizeObserver.disconnect();
        };
    }, [containerRef, contentRef, enableScroll]);

    return {
        contentRef,
    };
};
